/**
 * Utilidades para manejar precios con descuento de producto
 * El campo discount viene del API (lista y detalle) como porcentaje (ej: 5 para 5%)
 */

/**
 * Calcula el precio con descuento aplicado
 * @param {number} originalPrice - Precio original del producto
 * @param {number} discountPercent - Porcentaje de descuento (0-100)
 * @returns {number} Precio con descuento aplicado
 */
export const getPriceWithDiscount = (originalPrice, discountPercent) => {
  const price = Number(originalPrice) || 0;
  const discount = Number(discountPercent) || 0;
  if (discount <= 0) return price;
  const discountedPrice = price * (1 - discount / 100);
  return Number(discountedPrice.toFixed(2));
};

/**
 * Obtiene los datos de precio para mostrar (original, con descuento, porcentaje)
 * @param {number} price - Precio del producto (original)
 * @param {number} discount - Porcentaje de descuento del producto
 * @returns {{ originalPrice: number, discountedPrice: number, discountPercent: number, hasDiscount: boolean }}
 */
export const getPriceDisplayData = (price, discount) => {
  const originalPrice = Number(price) || 0;
  const discountPercent = Number(discount) || 0;
  const hasDiscount = discountPercent > 0 && originalPrice > 0;
  const discountedPrice = hasDiscount ? getPriceWithDiscount(originalPrice, discountPercent) : originalPrice;

  return {
    originalPrice,
    discountedPrice,
    discountPercent,
    hasDiscount,
  };
};

/**
 * Porcentaje inferido entre precio original y final (cuando no conocemos los % aplicados).
 */
export const getEffectiveDiscountPercent = (originalPrice, finalPrice) => {
  const original = Number(originalPrice) || 0;
  const final = Number(finalPrice) || 0;
  if (original <= 0 || final >= original - 0.004) return 0;
  return Math.round((1 - final / original) * 100);
};

/**
 * Desglose de precio unitario para una línea del carrito (descuento de producto y/o concurso).
 * Devuelve cada % por separado para mostrarlos en columna (normalmente 1 o 2).
 * @param {Object} cartItem - Línea del carrito con price, originalPrice, discount
 * @param {{ isConcourseUser?: boolean, concoursePercent?: number|null }} options
 */
export const getCartLinePriceDisplay = (cartItem, { isConcourseUser = false, concoursePercent = null } = {}) => {
  const finalPrice = Number(cartItem?.unit_price ?? cartItem?.price) || 0;
  const productDiscount = Number(cartItem?.discount) || 0;
  const concourseDiscount =
    isConcourseUser && concoursePercent != null && Number(concoursePercent) > 0
      ? Number(concoursePercent)
      : 0;

  let originalPrice = Number(cartItem?.originalPrice) || 0;
  if (!originalPrice && productDiscount > 0 && finalPrice > 0) {
    originalPrice = Number((finalPrice / (1 - productDiscount / 100)).toFixed(2));
  }
  if (!originalPrice && concourseDiscount > 0 && finalPrice > 0) {
    originalPrice = Number((finalPrice / (1 - concourseDiscount / 100)).toFixed(2));
  }
  if (!originalPrice) {
    originalPrice = finalPrice;
  }

  const priceAfterProduct =
    productDiscount > 0 ? getPriceWithDiscount(originalPrice, productDiscount) : originalPrice;
  const concourseApplied = concourseDiscount > 0 && priceAfterProduct > finalPrice + 0.004;

  const appliedDiscountPercents = [];
  if (productDiscount > 0) {
    appliedDiscountPercents.push(productDiscount);
  }
  if (concourseApplied) {
    appliedDiscountPercents.push(concourseDiscount);
  }
  if (appliedDiscountPercents.length === 0 && originalPrice > finalPrice + 0.004) {
    const effective = getEffectiveDiscountPercent(originalPrice, finalPrice);
    if (effective > 0) {
      appliedDiscountPercents.push(effective);
    }
  }

  const hasDiscount = appliedDiscountPercents.length > 0;

  return {
    originalPrice,
    finalPrice,
    appliedDiscountPercents,
    hasDiscount,
  };
};
